/**
 * Multi-select picker for notification channels.
 *
 * Lists every enabled channel with a checkbox; the in_app singleton is
 * always first (so it's obvious it's available even though the row
 * can't be deleted). Selected channel ids are surfaced as small chips
 * in the trigger button.
 *
 * Used by AlertRuleDialog (and any future "fire to these channels"
 * config). Built on Radix Popover so it composes safely inside dialogs.
 */
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, Megaphone } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/cn";
import { channelsApi, type Channel } from "@/lib/api-channels";


export interface ChannelPickerProps {
  value: string[];
  onChange: (ids: string[]) => void;
  /** Show only channels of these types (omit = all). */
  types?: Channel["type"][];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}


export function ChannelPicker({
  value,
  onChange,
  types,
  placeholder = "Pick channels",
  disabled,
  className,
  id,
}: ChannelPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const q = useQuery({
    queryKey: ["channels", "picker"],
    queryFn: () => channelsApi.list({ enabled: true }),
    staleTime: 30_000,
  });

  const rows = React.useMemo(() => {
    let r = q.data ?? [];
    if (types && types.length > 0) r = r.filter((c) => types.includes(c.type));
    // in_app singleton first, then alphabetical by name.
    return r.slice().sort((a, b) => {
      if (a.is_singleton && !b.is_singleton) return -1;
      if (!a.is_singleton && b.is_singleton) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [q.data, types]);

  const filtered = React.useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (c) =>
        c.name.toLowerCase().includes(s) ||
        c.type.toLowerCase().includes(s) ||
        (c.description ?? "").toLowerCase().includes(s),
    );
  }, [rows, search]);

  const byId = React.useMemo(() => {
    const m = new Map<string, Channel>();
    for (const c of rows) m.set(c.id, c);
    return m;
  }, [rows]);

  function toggle(id: string) {
    if (value.includes(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      onChange([...value, id]);
    }
  }

  const selectedCount = value.length;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-8 w-full items-center justify-between gap-2 rounded-md border border-border bg-surface px-2.5 text-xs",
            "hover:border-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent",
            disabled && "opacity-50",
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
            <Megaphone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
            {selectedCount === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              <span className="truncate">
                {value
                  .map((id) => byId.get(id)?.name ?? id.slice(0, 8))
                  .join(", ")}
              </span>
            )}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[18rem] rounded-md border border-border bg-elevated shadow-lg"
        >
          <div className="border-b border-border/60 p-1.5">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter channels…"
              className="h-7 w-full rounded bg-transparent px-2 text-xs outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {q.isLoading ? (
              <div className="px-2 py-2 text-xs text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="px-2 py-2 text-xs text-muted-foreground">
                {rows.length === 0
                  ? "No channels — create one in /channels."
                  : "No matches."}
              </div>
            ) : (
              filtered.map((c) => {
                const checked = value.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c.id)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs",
                      "hover:bg-muted",
                      checked && "bg-accent/10",
                    )}
                  >
                    <span
                      className={cn(
                        "grid h-4 w-4 shrink-0 place-items-center rounded border",
                        checked
                          ? "border-accent bg-accent text-accent-foreground"
                          : "border-border",
                      )}
                    >
                      {checked && <Check className="h-3 w-3" strokeWidth={2.5} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-foreground">
                        {c.name}
                      </span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {c.type}
                        {c.is_singleton && " · singleton"}
                        {c.description ? ` · ${c.description}` : ""}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
          {selectedCount > 0 && (
            <div className="flex items-center justify-between border-t border-border/60 px-2 py-1.5 text-[11px] text-muted-foreground">
              <span>
                {selectedCount} selected
              </span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="hover:text-foreground"
              >
                Clear
              </button>
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
